import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { formatCurrency } from '@/lib/utils';
import UniversalStatusBadge from '@/components/shared/UniversalStatusBadge';

interface OverdueInvoice {
  id: string;
  invoice_number: string | null;
  title: string;
  total_amount: number | null;
  paid_amount: number | null;
  due_date: string;
  customer: {
    name: string;
    last_name: string;
  } | null;
}

export const OverdueInvoicesWidget = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  
  const { data: overdueInvoices, isLoading } = useQuery({
    queryKey: ['overdue-invoices-widget'],
    queryFn: async () => {
      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          title,
          total_amount,
          paid_amount,
          due_date,
          customer:customers!invoices_customer_id_fkey(name, last_name)
        `)
        .eq('status', 'sent')
        .lt('due_date', now)
        .order('due_date', { ascending: true })
        .limit(5);
      
      if (error) throw error;
      return data as OverdueInvoice[];
    },
    staleTime: 30000,
    enabled: !!profile?.company_id,
  });

  const totalOverdue = overdueInvoices?.reduce((sum, inv) => {
    const outstanding = (inv.total_amount || 0) - (inv.paid_amount || 0);
    return sum + outstanding;
  }, 0) || 0;

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Overdue Invoices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!overdueInvoices?.length) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5 text-muted-foreground" />
            Overdue Invoices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-muted-foreground">
            <p className="text-sm">No overdue invoices</p>
            <p className="text-xs mt-1">Great job staying on top of payments!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-destructive/20">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Overdue Invoices
            <span className="ml-1 px-2 py-0.5 text-xs font-medium rounded-full bg-destructive/10 text-destructive">
              {overdueInvoices.length}
            </span>
          </CardTitle>
          <span className="text-sm font-semibold text-destructive">
            {formatCurrency(totalOverdue)} outstanding
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {overdueInvoices.map((invoice) => {
            const outstanding = (invoice.total_amount || 0) - (invoice.paid_amount || 0);
            const customerName = invoice.customer 
              ? `${invoice.customer.name} ${invoice.customer.last_name}`.trim()
              : 'Unknown Customer';
            
            return (
              <button
                key={invoice.id}
                onClick={() => navigate(`/invoices/${invoice.id}`)}
                className="w-full flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-left group"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">
                      {invoice.invoice_number || invoice.title}
                    </span>
                    <UniversalStatusBadge
                      entityType="invoice"
                      status="sent"
                      dateContext={invoice.due_date}
                      size="sm"
                      showTooltip={false}
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-muted-foreground truncate">
                      {customerName}
                    </span>
                    <span className="text-xs text-destructive">
                      • {formatDistanceToNow(new Date(invoice.due_date), { addSuffix: true })}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 pl-2">
                  <span className="font-semibold text-sm text-destructive">
                    {formatCurrency(outstanding)}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                </div>
              </button>
            );
          })}
        </div>
        
        {overdueInvoices.length >= 5 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-3"
            onClick={() => navigate('/invoices?status=overdue')}
          >
            View all overdue invoices
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default OverdueInvoicesWidget;
